import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { X, Keyboard, Lightning } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

// Mobile-first full-screen scanner for /lookup.
// Detects whether the scanned code is a location or an item and routes to the
// existing Lookup page with the correct tab.
//
// Detection rules (ordered):
//   starts with "LOC-"   → location tab
//   starts with "SKU-"   → item tab
//   starts with "VAR"    → item tab (variant barcode)
//   otherwise            → item tab (most barcodes are products)

export default function LookupScan() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);
  const [status, setStatus] = useState('starting');   // 'starting' | 'ready' | 'error' | 'manual'
  const [err, setErr] = useState(null);
  const [torch, setTorch] = useState(false);
  const [manualCode, setManualCode] = useState('');

  useEffect(() => {
    let cancelled = false;
    const reader = new BrowserMultiFormatReader();
    readerRef.current = reader;

    (async () => {
      try {
        const devices = await BrowserMultiFormatReader.listVideoInputDevices();
        if (cancelled) return;
        if (!devices.length) {
          setStatus('error');
          setErr('No camera found on this device');
          return;
        }
        // Prefer the rear camera
        const rear = devices.find((d) => /back|rear|environment/i.test(d.label)) || devices[devices.length - 1];
        await reader.decodeFromVideoDevice(rear.deviceId, videoRef.current, (result, e, controls) => {
          if (cancelled) { controls.stop(); return; }
          controlsRef.current = controls;
          if (result) {
            controls.stop();
            handleCode(result.getText());
          }
        });
        setStatus('ready');
      } catch (e) {
        setStatus('error');
        setErr(e?.message || 'Unable to access camera');
      }
    })();

    return () => {
      cancelled = true;
      // Stop active decoding loop + release camera hardware
      try { controlsRef.current?.stop?.(); } catch (_) { /* noop */ }
      try {
        const stream = videoRef.current?.srcObject;
        stream?.getTracks?.().forEach((t) => t.stop());
      } catch (_) { /* noop */ }
      readerRef.current = null;
      controlsRef.current = null;
    };
  }, []);

  const classifyAndGo = (code) => {
    const c = (code || '').trim();
    if (!c) return;
    const up = c.toUpperCase();
    let tab = 'item';
    if (up.startsWith('LOC-') || /^[A-Z]-\d{2,}/.test(up)) tab = 'location';
    if (up.startsWith('SKU-') || up.startsWith('VAR')) tab = 'item';
    navigate(`/lookup?tab=${tab}&q=${encodeURIComponent(c)}`, { replace: true });
  };

  const handleCode = (code) => {
    // small haptic on supported devices
    if (navigator.vibrate) navigator.vibrate(80);
    classifyAndGo(code);
  };

  const toggleTorch = async () => {
    try {
      const track = videoRef.current?.srcObject?.getVideoTracks?.()[0];
      if (!track) return;
      const caps = track.getCapabilities?.() || {};
      if (!caps.torch) return;
      await track.applyConstraints({ advanced: [{ torch: !torch }] });
      setTorch(!torch);
    } catch (_) { /* torch unsupported */ }
  };

  return (
    <div className="fixed inset-0 bg-black text-white z-50" data-testid="lookup-scan-page">
      <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" muted autoPlay playsInline />
      {/* Scan overlay */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <div className="w-72 h-72 border-4 border-brand/80" style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)' }} />
      </div>
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4 pointer-events-auto">
        <button data-testid="scan-close" onClick={() => navigate('/lookup')} className="w-10 h-10 bg-black/60 border border-white/30 flex items-center justify-center">
          <X size={20} weight="bold" />
        </button>
        <div className="text-xs uppercase tracking-widest font-bold bg-black/60 px-3 py-1 border border-white/30">
          {status === 'ready' ? 'Scan a location or item' : status === 'error' ? 'Camera error' : 'Starting camera…'}
        </div>
        <button data-testid="scan-torch" onClick={toggleTorch} className={`w-10 h-10 border flex items-center justify-center ${torch ? 'bg-amber-400 text-black border-amber-500' : 'bg-black/60 border-white/30'}`}>
          <Lightning size={20} weight={torch ? 'fill' : 'bold'} />
        </button>
      </div>
      {/* Bottom helper */}
      <div className="absolute bottom-0 left-0 right-0 p-4 space-y-2">
        {status === 'error' && (
          <div data-testid="scan-error" className="bg-rose-600 text-white p-3 text-xs uppercase tracking-widest font-bold text-center">
            {err || 'Scanner error'}
          </div>
        )}
        <div className="bg-black/70 border border-white/30 p-3">
          <div className="flex items-center gap-2">
            <Keyboard size={16} weight="bold" />
            <span className="text-[10px] uppercase tracking-widest font-bold">Can't scan? Type it:</span>
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); classifyAndGo(manualCode); }}
            className="flex gap-2 mt-2"
          >
            <Input
              data-testid="scan-manual-input"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder="LOC-A01 or SKU-ABC"
              className="flex-1 bg-card text-foreground font-mono uppercase"
            />
            <Button data-testid="scan-manual-submit" type="submit" className="bg-brand hover:bg-brand/90 text-white rounded-none font-bold">Go</Button>
          </form>
        </div>
      </div>
    </div>
  );
}
